import { Permission, UserRole } from '~/enums'
import { PAYOUT_ACCOUNTS_PATH, PAYOUT_ONBOARDING_PATH } from '~/constants/routes'
import { PAYOUT_ACCOUNT_PERMISSIONS } from '~/constants/payoutAccountPermissions'
import { hasAnyPermission } from '~/utils/hasAnyPermission'
import { NAV_ITEMS } from '~/utils/NAV_ITEMS'

const hasPermission = (permissions: readonly Permission[], permission: Permission): boolean =>
  hasAnyPermission(permissions, [permission])

/**
 * Matches a requested path against one nav definition route: exact, nested
 * (e.g. /orders/123), or the parent-section rule for deeply nested routes.
 */
const routeMatches = (cleanPath: string, cleanRoute: string): boolean => {
  if (cleanPath === cleanRoute) return true
  if (cleanPath.startsWith(`${cleanRoute}/`)) return true

  const routeSegments = cleanRoute.split('/')

  if (routeSegments.length >= 3) {
    const parentSection = routeSegments.slice(0, 3).join('/')

    if (cleanPath === parentSection || cleanPath.startsWith(`${parentSection}/`)) return true
  }

  return false
}

/**
 * Checks whether a user has access to a given route.
 *
 * The function:
 * - Normalizes the requested path (removes query params and trailing slashes)
 * - Always allows access to `/dashboard`
 * - Resolves the requested path against the nav definition's routes, gating
 *   each on the permissions that definition entry requires (any-of)
 * - Supports:
 *   - Exact route matches
 *   - Nested routes (e.g. `/orders/123`)
 *   - Parent section access for deeply nested routes
 *
 * @param requestedPath - The route path the user is trying to access
 * @param permissions - The permissions granted to the user
 * @param roles - The roles held by the user
 * @returns `true` if the user has access, otherwise `false`
 */
export const checkRouteAccess = (
  requestedPath = '',
  permissions: readonly Permission[] = [],
  roles: readonly UserRole[] = []
): boolean => {
  if (requestedPath.length === 0) return false

  // Remove query parameters and trailing slash for safe comparison
  const pathWithoutQuery = requestedPath.split('?')[0] ?? ''
  const cleanPath = pathWithoutQuery.replace(/\/$/, '')

  // Dashboard is always accessible
  if (cleanPath === '/dashboard') {
    return true
  }

  const isBrandCreate =
    cleanPath === '/dashboard/brands/create' || cleanPath === '/dashboard/brands/setup'
  const isBrandEdit = /^\/dashboard\/brands\/[^/]+\/edit$/.test(cleanPath)
  const isAccountSettingsRoute =
    cleanPath === '/dashboard/settings/account' ||
    cleanPath.startsWith('/dashboard/settings/account/')
  const isPayoutAccountsOnboardingRoute =
    cleanPath === PAYOUT_ONBOARDING_PATH || cleanPath.startsWith(`${PAYOUT_ONBOARDING_PATH}/`)
  const isPayoutAccountsRoute =
    cleanPath === PAYOUT_ACCOUNTS_PATH || cleanPath.startsWith(`${PAYOUT_ACCOUNTS_PATH}/`)
  const isStorePayoutRoute = /^\/dashboard\/stores\/[^/]+\/payout$/.test(cleanPath)
  const isPayoutsListRoute = cleanPath === '/dashboard/payouts'
  const isPayoutsRoute = cleanPath.startsWith('/dashboard/payouts/')

  if (isBrandCreate) {
    return hasPermission(permissions, Permission.CREATE_BRANDS)
  }

  if (isBrandEdit) {
    return hasPermission(permissions, Permission.EDIT_BRANDS)
  }

  if (isAccountSettingsRoute) {
    return hasPermission(permissions, Permission.VIEW_PARTNERS)
  }

  if (isPayoutAccountsOnboardingRoute || isPayoutAccountsRoute || isStorePayoutRoute) {
    return hasAnyPermission(permissions, PAYOUT_ACCOUNT_PERMISSIONS)
  }

  // The partner payouts list needs `view payouts` and excludes store managers, as the
  // endpoint does; a payout's detail follows the permission alone.
  if (isPayoutsListRoute) {
    return (
      !roles.includes(UserRole.STORE_MANAGER) && hasPermission(permissions, Permission.VIEW_PAYOUTS)
    )
  }

  if (isPayoutsRoute) {
    return hasPermission(permissions, Permission.VIEW_PAYOUTS)
  }

  for (const item of NAV_ITEMS) {
    const cleanRoute = item.route.replace(/\/$/, '')

    // Skip dashboard since it's already handled
    if (cleanRoute === '/dashboard') {
      continue
    }

    if (!routeMatches(cleanPath, cleanRoute)) {
      continue
    }

    if (item.permissions.length === 0) {
      return true
    }

    if (hasAnyPermission(permissions, item.permissions)) {
      return true
    }
  }

  const isUsersRoute = cleanPath === '/dashboard/users' || cleanPath.startsWith('/dashboard/users/')

  if (
    isUsersRoute &&
    (hasPermission(permissions, Permission.VIEW_USERS) ||
      hasPermission(permissions, Permission.VIEW_ORGANIZER_USERS))
  ) {
    return true
  }

  return false
}
